import { useQuery } from "@tanstack/react-query";
import { loadSupabase } from "@/lib/supabaseLazy";

/**
 * Reading the settings an admin manages, from the public site.
 *
 * These rows were being written and never read. `announcement_banner`,
 * `enrollment_open` and `site_name` all had working editors in the admin panel
 * and no consumer anywhere on the site, so saving them changed a row in the
 * database and nothing else. This hook is the missing half.
 *
 * `site_settings` is readable by anon for rows flagged `is_public`, so no new
 * policy or endpoint is needed.
 */

export type BannerType = "info" | "success" | "warning";

export type AnnouncementBanner = {
  enabled: boolean;
  text: string;
  type: BannerType;
};

const BANNER_TYPES: BannerType[] = ["info", "success", "warning"];

/** Settings change rarely and are read on every page, so they are cached. */
const SETTINGS_STALE_MS = 60_000;

export const useSiteSetting = <T,>(key: string, fallback: T) => {
  const query = useQuery({
    queryKey: ["site-setting", key],
    staleTime: SETTINGS_STALE_MS,
    queryFn: async (): Promise<unknown> => {
      const supabase = await loadSupabase();
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", key)
        .maybeSingle();
      if (error) throw error;
      return data?.value ?? null;
    },
  });

  return {
    value: (query.data ?? null) === null ? fallback : (query.data as T),
    isLoading: query.isLoading,
  };
};

/**
 * The announcement banner, normalised.
 *
 * The stored shape is admin-entered JSON, so every field is validated rather
 * than trusted: a half-saved row must not be able to render an empty strip or
 * an unstyled banner across every page of the site.
 */
export const useAnnouncementBanner = (): AnnouncementBanner | null => {
  const { value } = useSiteSetting<unknown>("announcement_banner", null);
  if (!value || typeof value !== "object") return null;

  const v = value as Record<string, unknown>;
  const text = typeof v.text === "string" ? v.text.trim() : "";
  if (v.enabled !== true || !text) return null;

  const type = BANNER_TYPES.includes(v.type as BannerType) ? (v.type as BannerType) : "info";
  return { enabled: true, text, type };
};

/** Whether the public enrollment form is accepting submissions. */
export const useEnrollmentOpen = (): { open: boolean; isLoading: boolean } => {
  const { value, isLoading } = useSiteSetting<boolean>("enrollment_open", true);
  return { open: value !== false, isLoading };
};

/** The academy's display name, as set in the admin panel. */
export const useSiteName = (): string => {
  const { value } = useSiteSetting<string>("site_name", "Bullio Academy");
  return typeof value === "string" && value.trim() ? value.trim() : "Bullio Academy";
};
